import { useMemo, useRef, useState } from 'react';
import striptags from 'striptags';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import { capitalizeString } from 'shared-helpers/common';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { wikiLangCodes } from 'lib/lang';
import { Button, ModalButtonWithForm } from 'components/common';
import { FormGroup, RichText, Select, Text } from 'components/common/form';

import type { FormValues } from 'informed';
import type { Course } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  action: 'add' | 'edit';
  buttonIsDropdown?: boolean;
  buttonStyle?: React.CSSProperties;
  buttonText: string;
  icon?: IconProp;
  languageVersion?: Course['language'];
  languageVersionData?: Course['languageVersions'][number];
  languageVersionLabel?: string;
  successMessage: string;
}

const SettingsAddOrEditLanguageVersion = ({
  action,
  buttonIsDropdown = false,
  buttonStyle,
  buttonText,
  icon,
  languageVersion,
  languageVersionData,
  languageVersionLabel,
  successMessage
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course },
    serverConfig,
    user
  } = useReduxState();

  const formApiRef = useRef<any>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);

  const aiEnabled = isAiConfigAvailableForUser({ serverConfig, user });

  const handleTranslate = async (selectedLanguage?: string) => {
    const targetLanguage =
      action === 'edit' ? languageVersion : selectedLanguage;
    if (!targetLanguage) return;
    setIsTranslating(true);
    try {
      const { data } = await api.post(
        `/admin/courses/${course.id}/language-versions/${targetLanguage}/ai-translations/generate`,
        { scope: 'courseMeta' }
      );
      if (data.translatedData?.['course__title']) {
        formApiRef.current?.setValue(
          'title',
          data.translatedData['course__title']
        );
      }
      if (data.translatedData?.['course__description']) {
        formApiRef.current?.setValue(
          'description',
          data.translatedData['course__description']
        );
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      values.language = values.language || languageVersion;

      await api.put(
        `/admin/courses/${course.id}/${action}-language-version`,
        values
      );

      const courses = await api.get(`/admin/courses?id=${course.id}`);
      dispatch.updatePageState({ course: courses.data.courses[0] });
      dispatch.addNotice({ text: successMessage, type: 'success' });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const existingLanguages = useMemo(() => {
    const existingLanguages = { [course.language]: true };
    Object.keys(course.languageVersions || {}).forEach((language) => {
      existingLanguages[language] = true;
    });
    return existingLanguages;
  }, [course.language, course.languageVersions]);

  const availableLanguages = useMemo(
    () =>
      wikiLangCodes.filter((language) => !existingLanguages[language.value]),
    [existingLanguages]
  );

  const translateButton = (
    <Button
      category="light"
      disabled={isTranslating || (action === 'add' && !selectedLanguage)}
      icon="wand-magic-sparkles"
      loading={isTranslating}
      onClick={() => handleTranslate(selectedLanguage)}
      size="small"
      style={{ marginRight: 'auto' }}
      text={messages.commonTranslate}
    />
  );

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon={icon}
      buttonIsDropdown={buttonIsDropdown}
      buttonSize="header"
      buttonStyle={buttonStyle}
      buttonText={buttonText}
      footerContent={aiEnabled ? translateButton : null}
      footerStyle={
        aiEnabled ? { display: 'flex', alignItems: 'center' } : undefined
      }
      formAllowEmptyStrings={true}
      initialValues={
        action === 'edit' && languageVersionData
          ? {
              title: languageVersionData.courseTitle,
              description: languageVersionData.courseDescription
            }
          : null
      }
      modalActionText={messages.commonSave}
      modalTitle={buttonText}
      modalWidth="medium"
      formOnValueChange={(values) => {
        if (action === 'add') setSelectedLanguage(values.language);
      }}
      onHide={() => {
        setIsTranslating(false);
        setSelectedLanguage(null);
      }}
      onSubmit={handleSubmit}
      pristineIsValid={false}
      setFormApi={(formApi) => {
        formApiRef.current = formApi;
      }}
    >
      {() => (
        <FormGroup
          label={
            action === 'add' ? messages.commonLanguage : languageVersionLabel
          }
        >
          <FormGroup htmlFor="language">
            {action === 'add' && (
              <Select
                field="language"
                id="language"
                isRequired={true}
                isSearchable={true}
                options={availableLanguages.map((option) => ({
                  label: capitalizeString(option.label),
                  value: option.value
                }))}
                style={{ maxWidth: 300 }}
              />
            )}
          </FormGroup>

          <FormGroup htmlFor="title" label={messages.coursesCourseTitle}>
            <Text
              field="title"
              id="title"
              placeholder={course.title}
              required={true}
            />
          </FormGroup>

          <FormGroup
            htmlFor="description"
            label={messages.coursesCourseDescription}
          >
            <RichText
              autoGrow={false}
              field="description"
              id="description"
              maxLength={3000}
              placeholder={striptags(course.description || '')}
            />
          </FormGroup>
        </FormGroup>
      )}
    </ModalButtonWithForm>
  );
};

export default SettingsAddOrEditLanguageVersion;
